<script setup lang="ts">

import { useNIRApplicationsStore } from "@/domains/home/services/nir/registration/stores";
import { onMounted, ref } from "vue";
import { dateTimeFormat } from "@/commons/composables/transformations.ts";
import AppModal from "@/components/ui/AppModal.vue";
import TaskView from "@/domains/home/services/nir/registration/views/TaskView.vue";
import router from "@/router.ts";
import { useInstitutionStore } from "@/domains/home/services/nir/associate-registers/stores";
import { useFormsStore } from "@/domains/home/services/nir/configurations/stores/forms.ts";
import Pagination from "@/commons/components/Pagination.vue";
import DataUpdateTaskView from "@/domains/home/services/nir/registration/views/DataUpdateTaskView.vue";

const loading = ref(false);
const viewModalOpen = ref(false);
const selectedTaskId = ref("")

const ernFilter = ref('')
const trackingNumberFilter = ref('')
const entityTypeFilter = ref('')
const subEntityTypeFilter = ref('')
const institutionFilter = ref('')
const statusFilter = ref('')

const page = ref(1)
const limit = ref(12)
const store = useNIRApplicationsStore()

const institutionStore = useInstitutionStore()
const configStore = useFormsStore()

onMounted(() => {
  fetch()
  store.fetchDataUpdateStatistics()
  localStorage.removeItem("applicationId")

  institutionStore.fetchInstitutions(1, 1000)
  configStore.fetchPrimaryEntityTypes()
})

function fetch(){
  loading.value = true
  store.fetchDataUpdateApplications(
      trackingNumberFilter.value,
      ernFilter.value,
      institutionFilter.value,
      entityTypeFilter.value,
      subEntityTypeFilter.value,
      statusFilter.value,
      page.value, limit.value)
      .then(() =>{
        loading.value = false
      })
}

function open(id:string){
  viewModalOpen.value = true
  selectedTaskId.value = id
}

function fetchSubTypes() {
  configStore.fetchSubEntityTypes(entityTypeFilter.value)
  fetch()
}

function update(pg: number) {
  page.value = pg
  fetch()
}


</script>

<template>
  <div class="flex-1 flex flex-col p-2">
    <div class="grid grid-cols-6 gap-2 py-2" v-if="store.stats">
      <div class="flex flex-col text-gray-500 bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.stats.draft}}</label>
        <label class="text-sm">Draft</label>
      </div>
      <div class="flex flex-col bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.stats.submitted}}</label>
        <label class="text-sm">Submitted</label>
      </div>
      <div class="flex flex-col text-primary bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.stats.assigned}}</label>
        <label class="text-sm">Assigned</label>
      </div>
      <div class="flex flex-col text-success-600 bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.stats.approved}}</label>
        <label class="text-sm">Approved</label>
      </div>
      <div class="flex flex-col text-danger-600 bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.stats.rejected}}</label>
        <label class="text-sm">Rejected</label>
      </div>
      <div class="flex flex-col bg-white rounded-lg shadow p-3 text-center justify-center items-center">
        <label class="text-2xl font-bold">{{store.stats.total}}</label>
        <label class="text-sm">Total</label>
      </div>
    </div>

    <div class="grid grid-cols-6 gap-2 py-2">
      <input
          v-model="trackingNumberFilter"
          @change="fetch"
          type="text"
          placeholder="Tracking Number"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"/>
      <input
          v-model="ernFilter"
          @change="fetch"
          type="text"
          placeholder="Search ERN ..."
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"/>
      <select
          v-model="entityTypeFilter"
          @change="fetchSubTypes"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option value="">Select Entity Type</option>
        <option v-for="(type, idx) in configStore.primaryEntityTypes" :key="idx" :value="type.id">
          {{ type.name }}
        </option>
      </select>

      <select
          v-model="subEntityTypeFilter"
          @change="fetch"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option value="">Select Sub Entity Type</option>
        <option v-for="(type, idx) in configStore.subEntityTypes" :key="idx" :value="type.id">
          {{ type.name }}
        </option>
      </select>

      <select
          v-model="institutionFilter"
          @change="fetch"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option value="">Select Associate Register</option>
        <option v-for="(institution, idx) in institutionStore.institutions" :key="idx" :value="institution.id">
          {{ institution.name }}
        </option>
      </select>
      <select
          v-model="statusFilter"
          @change="fetch"
          required
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option value="">Select Status</option>
        <option value="DRAFT">Draft</option>
        <option value="SUBMITTED">Submitted</option>
        <option value="ASSIGNED">Assigned</option>
        <option value="QUERIED">Queried</option>
        <option value="REGISTER_QUERIED">Register Queried</option>
        <option value="APPROVED">Approved</option>
        <option value="AUTO_ADMITTED">Auto Admitted</option>
      </select>

    </div>

    <div class="bg-white rounded shadow flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
          <tr>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              #
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Tracking Number
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              ERN
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Name
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Type
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Sub Type
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Associate Register
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Status
            </th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
              Date
            </th>
            <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200"></th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="(application, idx) in store.dataUpdateApplications"
              :key="idx"
              class="hover:bg-gray-50 transition">
            <td class="py-3 px-4 border-b border-gray-200 font-bold">
              <span>{{ idx + 1 }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 font-semibold">
              <span>{{ application.trackingNumber }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <span>{{ application.ern }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <span>{{ application.name.toUpperCase() }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <span>{{ application.type }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <span v-if="application.subType">{{ application.subType }}</span>
              <span v-else>--</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <span class="text-primary"> <i class="fas fa-bank"></i> {{ institutionStore.institutions?.find(i => i.id == application.institutionId)?.name || '--' }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200">
              <span class="text-xs font-medium px-3 py-1 rounded-full inline-block"
                    :class="{
                      'bg-gray-100 text-gray-400': application.status === 'DRAFT',
                      'bg-gray-100 text-gray-600': application.status === 'PENDING',
                      'bg-success-50 text-success-600 border border-success-200': application.status === 'PREAPPROVAL',
                      'bg-success-50 text-success-400 border border-success-100': application.status === 'SENT',
                      'bg-gray-100 text-gray-800': application.status === 'SUBMITTED',
                      'bg-primary-100 text-primary-800': application.status === 'ASSIGNED',
                      'bg-success-100 text-success-800': application.status === 'APPROVED',
                      'bg-success-100 text-success-900': application.status === 'AUTO_ADMITTED',
                      'bg-danger-100 text-danger-800': application.status === 'REJECTED',
                      'bg-warning-100 text-warning-800': application.status === 'QUERIED',
                    }">
                {{ application.status.toUpperCase() }}
              </span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 text-gray-400">
              <span>{{ dateTimeFormat(application.createdAt) }}</span>
            </td>
            <td class="py-3 px-4 border-b border-gray-200 text-gray-400">
              <div class="flex gap-2">
                <button class="btn-sm-primary" @click="open(application.id)"> <i class="fa fa-eye"></i> View</button>
              </div>
            </td>
          </tr>
          <tr v-if="store.applications && store.applications.length === 0">
            <td colspan="8" class="py-8 text-center text-gray-500">
              No applications submitted
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <Pagination
          :pagination="store.pagination"
          :page="page"
          :limit="limit"
          label="applications"
          @update="update"
      />
    </div>
  </div>
  <AppModal v-model="viewModalOpen" xl6>
    <DataUpdateTaskView :id="selectedTaskId"/>
  </AppModal>
</template>

<style scoped>

</style>